import { z } from "zod";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request } from "../request";
import { ClientOut, UsageOut, UserAccessOut, UsersListOut } from "@/lib/api/schemas/client";

class ClientsEndpoint {
  public static readonly basePath = "clients";
  public static readonly schemas = {
    list: {
      response: z.object({
        count: z.number(),
        items: z.array(ClientOut),
      }),
    },
    listUsers: {
      response: z.object({
        count: z.number(),
        items: z.array(UsersListOut),
      }),
    },
    userAccess: {
      response: UserAccessOut,
    },
    currentUsage: {
      response: UsageOut,
    },
  };

  constructor(private api: Auth) {}

  /**
   * Lists clients (tenants) for the superuser invitation picker.
   *
   * NOTE: `GET /api-front/clients/` is not in the OpenAPI contract yet — it is pending on
   * the backend. Until it ships this 404s (and the picker stays empty); no drift entry is
   * registered in `contractDrift.spec.ts` for it until then.
   */
  public list(params?: {
    search?: string;
    ordering?: string;
    limit?: number;
    offset?: number;
  }): ActionReturnType<z.infer<typeof ClientsEndpoint.schemas.list.response>> {
    return request({
      log: ["Clients", "list"],
      method: "get",
      path: [ClientsEndpoint.basePath],
      query: params,
      response: ClientsEndpoint.schemas.list.response,
    });
  }

  public listUsers(params?: {
    search?: string;
    ordering?: string;
    limit?: number;
    offset?: number;
  }): ActionReturnType<z.infer<typeof ClientsEndpoint.schemas.listUsers.response>> {
    return request({
      log: ["Clients", "listUsers"],
      method: "get",
      path: [ClientsEndpoint.basePath, "users"],
      query: params,
      response: ClientsEndpoint.schemas.listUsers.response,
    });
  }

  public getCurrentUsage(): ActionReturnType<z.infer<typeof ClientsEndpoint.schemas.currentUsage.response>> {
    return request({
      log: ["Clients", "getCurrentUsage"],
      method: "get",
      path: [ClientsEndpoint.basePath, "current_usage"],
      response: ClientsEndpoint.schemas.currentUsage.response,
    });
  }

  public getHistoricalUsage(
    year: number,
    month: number,
  ): ActionReturnType<z.infer<typeof ClientsEndpoint.schemas.currentUsage.response>> {
    return request({
      log: ["Clients", "getHistoricalUsage"],
      method: "get",
      path: [ClientsEndpoint.basePath, "usage", year, month],
      response: ClientsEndpoint.schemas.currentUsage.response,
    });
  }

  public blockUser(id: number): ActionReturnType<z.infer<typeof ClientsEndpoint.schemas.userAccess.response>> {
    return request({
      log: ["Clients", "blockUser"],
      method: "post",
      path: [ClientsEndpoint.basePath, "users", id, "block"],
      response: ClientsEndpoint.schemas.userAccess.response,
    });
  }

  public unblockUser(id: number): ActionReturnType<z.infer<typeof ClientsEndpoint.schemas.userAccess.response>> {
    return request({
      log: ["Clients", "unblockUser"],
      method: "post",
      path: [ClientsEndpoint.basePath, "users", id, "unblock"],
      response: ClientsEndpoint.schemas.userAccess.response,
    });
  }
}

export { ClientsEndpoint };
